import memoize from 'lodash/memoize';
import { z, ZodTypeAny } from 'zod';
import { useMemo } from 'react';
import { Box, SxProps, Theme, useTheme } from '@mui/material';
import {
    CollectionPredicates,
    ComparePredicates,
    conditionTitles,
    IArgumentMetadata,
    IConditionWithValue,
    IdentityPredicates,
    IPolicyCondition,
    Predicate,
    SpecialValues,
} from '@tymely/atoms';
import { PartialBy, typedMemo } from '@global/types';
import { stopProgagation } from '@tymely/utils/other';

import { PredicateButton } from './PredicateButton';
import { NoValueConditionEditor, NoValueWithSpecialConditionEditor } from './NoValueConditionEditor';
import { SingleDateValueConditionEditor, SingleValueConditionEditor } from './SingleValueConditionEditor';
import { RangeConditionEditor } from './RangeConditionEditor';
import CategoricalConditionEditor from './CategoricalConditionEditor/CategoricalConditionEditor';
import { ConditionEditorProps as BaseConditionEditorProps } from './BaseConditionEditor';

export type ConditionEditorProps<T extends IPolicyCondition> = PartialBy<BaseConditionEditorProps<T>, 'condition'>;

export const hasCategories = (arg: IArgumentMetadata): boolean => {
    return Boolean(arg.options?.categories && Object.keys(arg.options.categories).length > 0);
};

const zSpecial = z.enum(SpecialValues).array();

const numerical = () =>
    z
        .string()
        .or(z.number())
        .refine((val) => !isNaN(Number(val)) && !isNaN(parseFloat(String(val))), 'Expected number')
        .transform(Number);
const someDate = () => z.string().min(1, 'Expected date').transform(Number);

export const predicateValueValidator = (dtype: string, predicate: Predicate, hasCategories: boolean): ZodTypeAny => {
    if (IdentityPredicates.includes(predicate)) {
        return z.undefined();
    }
    if (predicate === 'match_regex' || predicate === 'not_match_regex') {
        return z.string().min(1);
    }

    if (dtype.toLocaleLowerCase() === 'none') {
        return z.undefined();
    }
    if (dtype.includes('|')) {
        return z.union(
            dtype.split('|').map((t) => predicateValueValidator(t.trim(), predicate, hasCategories)) as [
                ZodTypeAny,
                ZodTypeAny,
                ...ZodTypeAny[],
            ],
        );
    }

    if (predicate === 'equals' || predicate === 'not_equals') {
        if (dtype.startsWith('list[')) {
            const match = dtype.match(/list\[(.*)]?/);
            return z.array(predicateValueValidator(match![1], predicate, hasCategories)).min(1);
        }
        return ['int', 'float'].includes(dtype) ? z.number().or(numerical()) : z.string().min(1);
    }
    if (ComparePredicates.includes(predicate)) {
        if (dtype === 'datetime' && ['before_date', 'after_date'].includes(predicate)) return z.string().or(someDate());

        return z.number().or(numerical());
    }
    if (predicate === 'in_range' || predicate === 'out_of_range') {
        return z
            .object({
                lower_bound: z.number().or(numerical()),
                upper_bound: z.number().or(numerical()),
            })
            .refine((obj) => obj.upper_bound > obj.lower_bound, 'Invalid range');
    }
    if (hasCategories) {
        return z.string().min(1).or(z.array(z.string()).min(1));
    }
    if (CollectionPredicates.includes(predicate)) {
        return z.array(['int', 'float'].includes(dtype) ? numerical() : z.string()).min(1);
    }

    return z.string();
};

export const conditionValidator = memoize(
    (predicate: Predicate, arg: IArgumentMetadata) => {
        const valueWithSpecial = z.object({
            value: predicateValueValidator(arg.dtype, predicate, hasCategories(arg)),
            special: zSpecial.min(0),
        });
        return z.union([valueWithSpecial, z.null()], {
            errorMap: () => ({
                message: 'Value is required.',
            }),
        });
    },
    (predicate, arg) => `${predicate}-${arg.id}`,
);

export const validateCondition = (condition: IPolicyCondition, argumentMetadata: IArgumentMetadata) => {
    const validator = conditionValidator(condition.predicate, argumentMetadata);
    return validator.safeParse(condition.value);
};

const getConditionEditor = <T extends IPolicyCondition>({
    argumentMetadata,
    disableUnderline,
    setCondition,
    condition,
    disabled,
    withSpecialValues,
    disablePortal,
    onInputFocusChange,
    dontCareTitle,
    variant,
}: ConditionEditorProps<T>) => {
    if (!condition) {
        return (
            <PredicateButton
                title={dontCareTitle}
                setCondition={setCondition}
                argumentMetadata={argumentMetadata}
                variant={variant ?? 'outlined'}
                disabled={disabled}
                dontCareTitle={dontCareTitle}
                fullWidth
            />
        );
    }

    const partialProps = {
        withSpecialValues,
        disableUnderline,
        disablePortal,
        setCondition,
        argumentMetadata,
        disabled,
        dontCareTitle,
    };

    switch (condition.predicate) {
        case 'is_true':
        case 'is_false':
            return (
                <NoValueWithSpecialConditionEditor
                    title={conditionTitles[condition.predicate]}
                    condition={condition}
                    {...partialProps}
                />
            );
        case 'has_items':
        case 'is_empty':
        case 'exists':
        case 'missing':
        case 'is_neither':
        case 'is_unknown':
        case 'is_unspecified':
            return (
                <NoValueConditionEditor
                    title={conditionTitles[condition.predicate]}
                    condition={condition}
                    {...partialProps}
                />
            );
        case 'equals':
        case 'not_equals':
            if (hasCategories(argumentMetadata)) {
                return (
                    <CategoricalConditionEditor
                        title={conditionTitles[condition.predicate]}
                        condition={condition as IConditionWithValue<'equals' | 'not_equals', string>}
                        categories={argumentMetadata.options?.categories}
                        {...partialProps}
                    />
                );
            }
            return (
                <SingleValueConditionEditor
                    title={conditionTitles[condition.predicate]}
                    condition={condition}
                    onInputFocusChange={onInputFocusChange}
                    {...partialProps}
                />
            );
        case 'greater_than':
        case 'less_than':
        case 'greater_or_equals':
        case 'less_or_equals':
        case 'match_regex':
        case 'not_match_regex':
        case 'length_equals':
        case 'length_not_equals':
        case 'longer_than':
        case 'shorter_than':
        case 'days_passed_greater_or_equals':
        case 'days_passed_less':
            return (
                <SingleValueConditionEditor
                    title={conditionTitles[condition.predicate]}
                    condition={condition}
                    onInputFocusChange={onInputFocusChange}
                    {...partialProps}
                />
            );
        case 'before_date':
        case 'after_date':
            return (
                <SingleDateValueConditionEditor
                    title={conditionTitles[condition.predicate]}
                    condition={condition}
                    onInputFocusChange={onInputFocusChange}
                    {...partialProps}
                />
            );
        case 'in_range':
        case 'out_of_range':
            return (
                <RangeConditionEditor
                    title={conditionTitles[condition.predicate]}
                    condition={condition}
                    onInputFocusChange={onInputFocusChange}
                    {...partialProps}
                />
            );
        case 'is_in':
        case 'not_in':
        case 'is_subset':
        case 'is_not_subset':
        case 'intersects':
        case 'not_intersects':
        case 'is_superset':
        case 'not_superset':
            return (
                <CategoricalConditionEditor
                    title={conditionTitles[condition.predicate]}
                    condition={condition}
                    categories={argumentMetadata.options?.categories}
                    {...partialProps}
                />
            );
    }
};

export const ConditionEditor = typedMemo(
    <T extends IPolicyCondition>(props: ConditionEditorProps<T> & { sx?: SxProps<Theme>; error?: string }) => {
        const theme = useTheme();
        const ConcreteConditionEditor = useMemo(() => getConditionEditor({ ...props }), [props]);

        return (
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: 1,
                }}
                onKeyDown={stopProgagation}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: '100%',
                        height: 1,
                        cursor: props.disabled ? 'default' : 'pointer',
                        ...(props.disabled && {
                            opacity: 0.6,
                            cursor: 'not-allowed',
                        }),
                        ...props.sx,
                    }}
                >
                    {ConcreteConditionEditor}
                </Box>
                {props.error && (
                    <Box
                        sx={{
                            display: 'flex',
                            position: 'absolute',
                            bottom: -22,
                            left: 0,
                            px: 1.5,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: theme.palette.error.main,
                        }}
                    >
                        {props.error}
                    </Box>
                )}
            </Box>
        );
    },
);
